import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import { IconSizes } from '../../../components/foundations/Icon';
import { SECTION_ACCESS_TYPES } from '../../../utils/consts';
import { buildAccessTypesRecord, buildPartDisplayName, getPartAccessType, SECTIONS_PAGES_CONFIG } from '../../sections';
import { CUSTOM_ACCESS_TYPE, translationPrefix as translationRootPrefix } from '../../utils';
import AccessTypeIcon from '../AccessTypeIcon';
import css from './SectionsReviewContent.css';

const translationPrefix = `${translationRootPrefix}.MANAGE_PERMISSIONS_PAGE`;

const AccessLine = ({ accessType, displayName }) => (
    <div className={css.partItem}>
        <AccessTypeIcon accessType={accessType} size={IconSizes.X_SMALL} withTooltip={false} interactive={false} />
        <span className={css.accessLabel}>
            <Translate id={`${translationPrefix}.SECTION_ACCESS_TYPES_TITLE.${accessType}`} />
        </span>
        <span className={css.partName}>{displayName}</span>
    </div>
);

AccessLine.propTypes = {
    accessType: PropTypes.number.isRequired,
    displayName: PropTypes.string.isRequired,
};

const PartItem = ({ part, accessTypesRecord, translate }) => {
    const partAccessType = getPartAccessType(part, accessTypesRecord);

    if (partAccessType === CUSTOM_ACCESS_TYPE) {
        return (
            <>
                <div className={css.partGroupTitle}>{buildPartDisplayName(part, partAccessType, translate)}</div>
                {part.sectionNames.map(sectionName => {
                    const sectionAccessType = accessTypesRecord[sectionName] || SECTION_ACCESS_TYPES.VIEW;
                    return (
                        <AccessLine
                            key={sectionName}
                            accessType={sectionAccessType}
                            displayName={buildPartDisplayName(part, sectionAccessType, translate, [sectionName])}
                        />
                    );
                })}
            </>
        );
    }

    return (
        <AccessLine accessType={partAccessType} displayName={buildPartDisplayName(part, partAccessType, translate)} />
    );
};

PartItem.propTypes = {
    part: PropTypes.shape({
        sectionNames: PropTypes.arrayOf(PropTypes.string).isRequired,
        displayName: PropTypes.string,
    }).isRequired,
    accessTypesRecord: PropTypes.objectOf(PropTypes.number).isRequired,
    translate: PropTypes.func.isRequired,
};

const SectionsReviewContent = ({ stepName, sectionPermissions, translate }) => {
    const stepConfig = SECTIONS_PAGES_CONFIG.find(c => c.step === stepName);

    const allSectionNames = useMemo(
        () => (stepConfig ? stepConfig.cards.flatMap(card => card.parts.flatMap(part => part.sectionNames)) : []),
        [stepConfig]
    );
    const accessTypesRecord = useMemo(() => buildAccessTypesRecord(allSectionNames, sectionPermissions), [
        allSectionNames,
        sectionPermissions,
    ]);

    if (!stepConfig) return null;

    const hasMultipleCards = stepConfig.cards.length > 1;

    return (
        <div>
            {stepConfig.cards.map(card => (
                <div key={card.title} className={css.cardGroup}>
                    {hasMultipleCards && (
                        <div className={css.cardTitle}>
                            <Translate id={card.title} />
                        </div>
                    )}
                    <div className={css.partsGrid}>
                        {card.parts.map(part => (
                            <PartItem
                                key={part.sectionNames.join('-')}
                                part={part}
                                accessTypesRecord={accessTypesRecord}
                                translate={translate}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

SectionsReviewContent.propTypes = {
    stepName: PropTypes.string.isRequired,
    sectionPermissions: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            access_type: PropTypes.number,
        })
    ).isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(SectionsReviewContent);
